
import { SelectedTicketInfo } from "../../../logic/models";
import { head, uniq, uniqueId, values } from "lodash";
import { TicketModel } from "../../../mvcapi/models/buyticket/submodels";
import { getMaximumNbrTicketsByEventDateId, getTicketModelsByEventDateId } from "../../../logic/ticket-management";

export enum AddToCartItemType {
    Ticket = "ticket", // Single ticket
    TicketPack = "ticketpack", // Flex Ticket Pad. When you buy a pack of 10 flex tickets.
    PackageDate = "packagedate", // Package Deal
    Package = "package", // Package Deal
}

export interface AddToCartItem {
    /** This ID is random for only internal in-memory manipulation */
    _id: string;
    type: AddToCartItemType;
}

export interface EventDateCartItem extends AddToCartItem {
    eventDateId: number;
}

// Regular event
export interface AddToCartTicketItem extends EventDateCartItem {
    type: AddToCartItemType.Ticket | AddToCartItemType.PackageDate;
    ticket: SelectedTicketInfo;
}

// Flex Ticket Pad
export interface AddToCartTicketPackItem extends EventDateCartItem {
    type: AddToCartItemType.TicketPack;
    tickets: SelectedTicketInfo[];
}

// Packages
export interface AddToCartPackageItem extends AddToCartItem {
    type: AddToCartItemType.Package;
    packageId: number;
    ticketNames: string;
    sectionName: string; // Adm. Gen ??
    fixedSeat: boolean;
    packageDateItems: AddToCartPackageDateItem[];
}

export interface AddToCartPackageDateItem extends AddToCartTicketItem {
    type: AddToCartItemType.PackageDate;
    fixedSeat: boolean;

    // This would be circular state. It don't know if it's handled properly in a state machine.
    //packageItem: AddToCartPackageItem;
}


// Logic
export function getTicketQuantity(item: AddToCartItem) {
    if(item.type === AddToCartItemType.Ticket) {
        return 1;
    } else if(item.type === AddToCartItemType.TicketPack) {
        return (item as AddToCartTicketPackItem).tickets.length;
    } else if(item.type === AddToCartItemType.Package) {
        return (item as AddToCartPackageItem).packageDateItems.reduce((acc, item) => acc + getTicketQuantity(item), 0);
    } else if(item.type === AddToCartItemType.PackageDate) {
        return (item as AddToCartPackageDateItem).ticket != null ? 1 : 0;
    }
}

export function getItemPriceIds(item: AddToCartItem) {
    const tickets = getTicketsFromItem(item);
    return tickets.map(ticket => ticket.priceId);
}

export function areCartItemsComplete(items: AddToCartItem[]) {
    return items.every(item => isCartItemComplete(item));
}

export function isCartItemComplete(item: AddToCartItem) {
    if(item.type === AddToCartItemType.Ticket) {
        return (item as AddToCartTicketItem).ticket != null;
    } else if(item.type === AddToCartItemType.TicketPack) {
        return (item as AddToCartTicketPackItem).tickets.length > 0;
    } else if(item.type === AddToCartItemType.Package) {
        return (item as AddToCartPackageItem).packageDateItems.every(item => isCartItemComplete(item));
    } else if(item.type === AddToCartItemType.PackageDate) {
        return (item as AddToCartPackageDateItem).ticket != null;
    }
}

export function findItemById(items: AddToCartItem[], id: string) {
    return items.find(it => it._id === id);
}

export function findItemsByTicketIds(items: AddToCartItem[], ticketIds: number[]) {

    return items.filter(item => {
        const tickets = getTicketsFromItem(item);
        return tickets.some(ticket => ticketIds.includes(ticket.ticketId));
    });
}

export function findItemByTicketId(items: AddToCartItem[], ticketId: number) {
    return items.find(it => it.type === AddToCartItemType.Ticket && (it as AddToCartTicketItem).ticket.ticketId === ticketId);
}

export function findTicketInItemsByTicketId(items: AddToCartItem[], ticketId: number): SelectedTicketInfo {
    for(let item of items) {
        const tickets = getTicketsFromItem(item);
        const ticket = tickets.find(ticket => ticket.ticketId === ticketId);
        if(ticket) return ticket;
    }
    return null;
}

export function getAddToCartItemByPackageId(items: AddToCartItem[], packageId: number): AddToCartItem[] {
    const packageItems: AddToCartItem[] = [];

    items.forEach(item => {
        const dateItem = getPackageItemFromItem(item, packageId);
        if(dateItem) packageItems.push(dateItem);
    });

    return packageItems;
}

export function getPackageItemFromItem(item: AddToCartItem, packageId: number): AddToCartItem {
    if(item.type === AddToCartItemType.Package) {
        if((item as AddToCartPackageItem).packageId === packageId) 
        return item as AddToCartPackageItem;
    }
    return null;
}

export function getAddToCartItemByEventDateId(items: AddToCartItem[], eventDateId: number): AddToCartItem[] {
    const dateItems: AddToCartItem[] = [];

    items.forEach(item => {
        const dateItem = getEventDateItemFromItem(item, eventDateId);
        if(dateItem) dateItems.push(dateItem);
    });

    return dateItems;
}

export function getTicketsByEventDateId(items: AddToCartItem[], eventDateId: number): SelectedTicketInfo[] {
    const dateItems: AddToCartItem[] = [];

    items.forEach(item => {
        const dateItem = getEventDateItemFromItem(item, eventDateId);
        if(dateItem) dateItems.push(dateItem);
    });

    return dateItems.flatMap(item => {
        const tickets = getTicketsFromItem(item);
        return tickets;
    });
}

export function getEventDateItemsFromItems(items: AddToCartItem[], eventDateId: number): AddToCartItem[] {
    return items.filter(item => getEventDateItemFromItem(item, eventDateId) != null);    
}


export function getEventDateItemFromItem(item: AddToCartItem, eventDateId: number): AddToCartItem {
    if((item.type === AddToCartItemType.Ticket || item.type === AddToCartItemType.TicketPack || item.type === AddToCartItemType.PackageDate) 
        && (item as EventDateCartItem).eventDateId === eventDateId) {
        return item;
    } else if(item.type === AddToCartItemType.Package) {
        return getPackageDateItemByEventDateId(item as AddToCartPackageItem, eventDateId);
    }
    return null;
}

/**
 * Return all items that belongs to event dates. No filters. Ex.: It won't return the package item, but its package date items instead.
 */
export function getAllEventDateItemsFromItems(items: AddToCartItem[]): AddToCartItem[] {
    return items.flatMap(item => getAllEventDateItemsFromItem(item));
}

export function getAllEventDateItemsFromItem(item: AddToCartItem): AddToCartItem[] {
    if((item.type === AddToCartItemType.Ticket || item.type === AddToCartItemType.TicketPack || item.type === AddToCartItemType.PackageDate)) {
        return [item];
    } else if(item.type === AddToCartItemType.Package) {
        return (item as AddToCartPackageItem).packageDateItems; // Return all
    }
    return [];
}

export function getTicketsFromItems(items: AddToCartItem[]): SelectedTicketInfo[] {
    return items.flatMap(item => getTicketsFromItem(item));
}

export function getTicketIdsFromItems(items: AddToCartItem[]): number[] {
    return getTicketsFromItems(items).map(ticket => ticket.ticketId);
}

export function getTicketsFromItem(item: AddToCartItem): SelectedTicketInfo[] {
    if(item.type === AddToCartItemType.Ticket) {
        return (item as AddToCartTicketItem).ticket != null ? [ (item as AddToCartTicketItem).ticket ] : [];
    } else if(item.type === AddToCartItemType.TicketPack) {
        return (item as AddToCartTicketPackItem).tickets;
    } else if(item.type === AddToCartItemType.Package) {
        return (item as AddToCartPackageItem).packageDateItems.reduce((acc, item) => acc.concat(getTicketsFromItem(item)), []);
    } else if(item.type === AddToCartItemType.PackageDate) {
        return (item as AddToCartPackageDateItem).ticket != null ? [ (item as AddToCartPackageDateItem).ticket ] : [];
    }
}

export function getPackageDateItemsByEventDateId(packageItems: AddToCartPackageItem[], eventDateId: number): AddToCartPackageDateItem[] {
    return packageItems.map(packageItem => getPackageDateItemByEventDateId(packageItem, eventDateId)).filter(item => item != null);
}

export function getPackageDateItemByEventDateId(packageItem: AddToCartPackageItem, eventDateId: number): AddToCartPackageDateItem {
    return packageItem.packageDateItems.find(item => item.eventDateId === eventDateId);
}


export function getCartTicketItemFromTicket(eventDateId: number, ticket: SelectedTicketInfo): AddToCartItem {
    return {
        _id: ticket.ticketId.toString(),
        type: AddToCartItemType.Ticket,
        eventDateId: eventDateId,
        ticket: ticket
    } as AddToCartTicketItem;
}

export function getCartTicketPackItemFromTickets(eventDateId: number, tickets: SelectedTicketInfo[]): AddToCartItem {
    return {
        _id: tickets.map(t => t.ticketId).join(","),
        type: AddToCartItemType.TicketPack,
        eventDateId: eventDateId,
        tickets: tickets
    } as AddToCartTicketPackItem;
}
// TODO : utiliser les selectedEventDateIds au lieu de la trouver à partir des tickets
export function getCartPackageItemsFromTicketModels(packageId: number, selectedEventDateIds: number[], tickets: TicketModel[], fixedSeat: boolean): AddToCartItem[] {
    if(!tickets && tickets.length === 0) return [];

    // Créer un dictionnaire de tickets par eventDateId
    const ticketsByEventDateId = getTicketModelsByEventDateId(tickets);

    // Trouver le nombre maximum de billets par eventDateId (va servir a créer le nombre de packageItems) 
    const nbrPackageItems = getMaximumNbrTicketsByEventDateId(ticketsByEventDateId);

    if(fixedSeat) {
        const uniqTicketIds = uniq(tickets.map(t => t.TicketID));
        if(uniqTicketIds.length !== tickets.length) {
            console.error("Duplicate ticketID found in the list of tickets. Cannot add selected package items.");
            throw new Error("Duplicate ticketID found in the list of tickets. Cannot add selected package items.");
        }

        values(ticketsByEventDateId).forEach((tickets) => {
            if(tickets.length !== nbrPackageItems) {
                console.error("Number of tickets per eventDateId is not the same in all event dates. Cannot add selected package items.");
                throw new Error("Number of tickets per eventDateId is not the same in all event dates. Cannot add selected package items.");
            }
        });
    }

    // Créer autant de packageItems que le nombre de tickets par eventDateId
    let packageItems = [] as AddToCartPackageItem[];
    
    // Boucle dans chaques dates d'événements
    // On commence par boucler dans les dates sélectionnées pour supporter le forfait non fixe sans billet trouvé dans une date d'événement
    // TODO: Forfait Non-Fixe créer les package items avec ticket sans billet
    selectedEventDateIds.forEach((eventDateId) => {

        // Boucle le nombre maximum de billets trouver plus haut dans les billets trouvés
        // Sert à créer un packageItem par billet trouver dans chaque date d'événement
        for(let i = 0; i < nbrPackageItems; i++) {

            // Si le packageItem n'existe pas, on le crée
            if(!packageItems[i]) {
                packageItems[i] = {
                    _id: uniqueId(),
                    type: AddToCartItemType.Package,
                    packageId: packageId,
                    fixedSeat: fixedSeat,
                    packageDateItems: [],
                    ticketNames: "",
                    sectionName: ""
                } as AddToCartPackageItem;
            }
            
            let packageItem = packageItems[i];
            // On ajoute un package date items pour chaque date et les ajoute au packageItem
            if(ticketsByEventDateId[eventDateId][i])
                packageItem.packageDateItems.push(getCartPackageDateItemFromTicketByEventDateId(ticketsByEventDateId[eventDateId][i], eventDateId, fixedSeat));
        
            // Trouve les noms de billets uniques pour le packageItem et les lie par une virgule servira à l'affichage d'un packageItem
            const uniqTicketNames = uniq(packageItem.packageDateItems.map(t => t.ticket.ticket.Name)).join(", ");
            packageItem.ticketNames = uniqTicketNames;
        }        
    });

    return packageItems;
}

export function getCartPackageDateItemFromTickets(eventDateId: number, eventDateTickets: TicketModel[], fixedSeat: boolean): AddToCartPackageDateItem[] {
    return eventDateTickets.map((ticket) => { 
        return { 
            _id: ticket.TicketID.toString(),
            type: AddToCartItemType.PackageDate,
            eventDateId: eventDateId,
            ticket: { ticketId: ticket.TicketID, ticket: ticket },
            fixedSeat: fixedSeat
        } as AddToCartPackageDateItem;
    });
}


export function getCartPackageDateItemFromTicketByEventDateId(ticket: TicketModel, eventDateId: number, fixedSeat: boolean): AddToCartPackageDateItem {
    return { 
    _id: ticket.TicketID.toString(),
    type: AddToCartItemType.PackageDate,
    eventDateId: eventDateId,
    ticket: { ticketId: ticket.TicketID, ticket: ticket },
    fixedSeat: fixedSeat
    } as AddToCartPackageDateItem;
}

/** Va servir pour les flex */
export function getCartTicketPackItemFromTicket(tickets: SelectedTicketInfo[]): AddToCartItem {
    return {
        _id: tickets[0].ticketId.toString(),
        type: AddToCartItemType.TicketPack,
        tickets: tickets,

    } as AddToCartTicketPackItem;
}

export function getPackageDateItemsByPricegroupName(item: AddToCartPackageItem): { [pricegroupName: string]: AddToCartPackageDateItem[] } {
    let packageDateItemsByPricegroupName: { [pricegroupName: string]: AddToCartPackageDateItem[] } = {};

    item.packageDateItems.forEach(packageDateItem => {
        const pricegroupName = packageDateItem.ticket.pricegroup.Name;
        if(!packageDateItemsByPricegroupName[pricegroupName]) packageDateItemsByPricegroupName[pricegroupName] = [];
        packageDateItemsByPricegroupName[pricegroupName].push(packageDateItem);
    });

    return packageDateItemsByPricegroupName;
}

export function getAllAddToCartItemsFromTickets(eventDateId: number, tickets: SelectedTicketInfo[]): AddToCartItem[] {


    return tickets.map(ticket => ({
        _id: ticket.ticketId.toString(),
        type: AddToCartItemType.Ticket,
        eventDateId: eventDateId,
        ticket: ticket,
        pricegroup: ticket.pricegroup ? ticket.pricegroup : null
    }));
}